import React, { useEffect, useState, useRef, useContext } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Button,
  TextField,
  Autocomplete,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from "notistack";
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import UserContext from "../../context/user-context";


import AttachmentIcon from "@mui/icons-material/Attachment";
import TopicIcon from '@mui/icons-material/Topic';
import AddIcon from '@mui/icons-material/Add'; // Importing Add icon

import { Icon } from '@fluentui/react/lib/Icon';
import { getFileTypeIconProps, initializeFileTypeIcons } from '@fluentui/react-file-type-icons'; // Ensure this line is correct
import httpRequest from '../../httpRequest';

const FileExplorer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const userCtx = useContext(UserContext);
  const { overriddenRole, employeeData, userId } = userCtx;
  // Initialize state variables
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentContextMenu, setDocumentContextMenu] = useState(null);
  const [categoryContextMenu, setCategoryContextMenu] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null); // Add this line to define selected category state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // State for adding and editing categories
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [categoryData, setCategoryData] = useState({ name: '', description: '' });
  const [currentCategoryId, setCurrentCategoryId] = useState(null);

  const [openAddDocumentDialog, setOpenAddDocumentDialog] = useState(false);
const fileInputRef = useRef(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: "",
    description: "",
    filename: "",
    file: null,
    category: null,
    company: null,
    function: null,
  });



  useEffect(() => {
    initializeFileTypeIcons();
    fetchDocuments();
    fetchCategories();
  }, []);

  // Fetch documents and categories
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/documents?exclude_fields=file`);
      setDocuments(response.data);
      setFilteredDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/document-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const handleAddDocument = () => {
    setOpenAddDocumentDialog(true);
  };


  const handleAddCategory = () => {
    setCategoryData({ name: '', description: '' });
    setOpenAddDialog(true);
  };

  const handleEditCategory = (category) => {
    setCategoryData({ name: category.name, description: category.description });
    setCurrentCategoryId(category.id);
    setOpenEditDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    handleCloseContextMenu(); // Close the context menu immediately
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/document-categories/${id}`);
      fetchCategories(); // Refresh category list after deletion
    } catch (error) {
      enqueueSnackbar("This category is used by the system and can't be deleted", { variant: "warning" });
      console.error("Error deleting category:", error);
    }
  };
  

  const handleAddDocumentCategory = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/document-categories`, categoryData);
      fetchCategories();
      setOpenAddDialog(false);
    } catch (error) {
      console.error("Error adding document category:", error);
    }
  };

  const handleUpdateDocumentCategory = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/document-categories/${currentCategoryId}`, categoryData);
      fetchCategories();
      setOpenEditDialog(false);
    } catch (error) {
      console.error("Error updating document category:", error);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category); // Set the selected category state
    setFilteredDocuments(documents.filter(doc => doc.category.id === category.id));
  };
  
  

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setDetailsDialogOpen(true);
  };

  const handleDocumentRightClick = (event, doc) => {
    event.preventDefault();
    setDocumentContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      document: doc,
    });
    setCategoryContextMenu(null); // Ensure the category menu is closed
  };
  
  const handleCategoryRightClick = (event, category) => {
    event.preventDefault();
    setCategoryContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      category,
    });
    setDocumentContextMenu(null); // Ensure the document menu is closed
  };

  const handleCloseContextMenu = () => {
    setDocumentContextMenu(null); // Close document context menu
    setCategoryContextMenu(null); // Close category context menu
  };
  


  const handleView = (doc) => {
    setSelectedDocument(doc);
    setDetailsDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleEdit = (doc) => {
    setSelectedDocument(doc);
    setEditDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleDelete = async (doc) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/documents/${doc.id}`);
        fetchDocuments();
        setFilteredDocuments(filteredDocuments.filter(item => item.id !== doc.id));
      } catch (error) {
        console.error("Failed to delete document", error);
      }
    }
    handleCloseContextMenu();
  };


  const handleAttachmentClick = async (doc) => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/documents?id=${doc.id}`);
      if (response.data.file) {
        const byteCharacters = atob(response.data.file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: getMimeType(doc.filename) });
        const blobUrl = URL.createObjectURL(blob);
        if (doc.filename.endsWith('.pdf')) {
          window.open(blobUrl, '_blank', 'width=800,height=600');
        } else if (doc.filename.match(/\.(jpg|jpeg|png|gif)$/)) {
          const iframeWindow = window.open('', '_blank', 'width=800,height=600');
          iframeWindow.document.write(`<iframe src="${blobUrl}" style="width:100%; height:100%;" frameborder="0"></iframe>`);
        } else {
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = doc.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } else {
        console.error("File data not available in the response.");
      }
    } catch (error) {
      console.error("Failed to open attachment", error);
    }
  };

  const getMimeType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      default:
        return 'application/octet-stream';
    }
  };

  const handleSaveChanges = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/documents/${selectedDocument.id}`, {
        name: selectedDocument.name,
        description: selectedDocument.description,
        document_category_id: selectedDocument.category.id, // Assuming category is selected correctly
        filename: selectedDocument.filename,
      });
      setEditDialogOpen(false); // Close dialog after saving
      fetchDocuments(); // Refresh document list
    } catch (error) {
      console.error("Failed to save changes", error);
    }
  };

  const handleCategoryDropdownOpen = async () => {
    if (categories.length === 0) {
      await fetchCategories();
    }
  };

  

 const filteredCompanies = employeeData ? Object.values(employeeData.companies) : [];
const filteredFunctions = employeeData ? Object.values(employeeData.functions) : [];

  
  const defaultCompany = employeeData
  ? { id: employeeData.company_id, company_name: employeeData.company_name }
  : null;

const defaultFunction = employeeData
  ? { id: employeeData.function_id, function_name: employeeData.function_name }
  : null;

  
   
    
    const handleButtonClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click(); // Trigger the file input click
      }
    };
  

const handleFileChange = (e) => {
  const file = e.target.files[0];
  const reader = new FileReader();
  reader.onload = () => {
    const base64Data = reader.result.split(",")[1];
    setNewDocument({ ...newDocument, file: base64Data, filename: file.name });
  };
  reader.readAsDataURL(file);
};


const handleUpload = async () => {
  setFormSubmitted(true);

    if (!newDocument.file || !newDocument.name || !newDocument.category) {
      enqueueSnackbar("Please fill all required fields", { variant: "warning" });
      return;
    }



  let updatedNewDocument = { ...newDocument };

  // If there's only one company, set it automatically
  if (filteredCompanies.length === 1) {
    updatedNewDocument.company = filteredCompanies[0];
  }

  // If there's only one function, set it automatically
  if (filteredFunctions.length === 1) {
    updatedNewDocument.function = filteredFunctions[0];
  }

  const payload = {
    name: updatedNewDocument.name,
    description: updatedNewDocument.description,
    filename: updatedNewDocument.filename,
    file: updatedNewDocument.file, // This is Base64
    document_category_id: updatedNewDocument.category.id,
    company_id: updatedNewDocument.company?.id,
    function_id: updatedNewDocument.function?.id,
    uploaded_by_user_id: userId,
  };

  try {
    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/documents`, payload);
    enqueueSnackbar("Document uploaded successfully", { variant: "success" });
    setFormSubmitted(false);
    setNewDocument({
      name: "",
      description: "",
      file: null,
      filename: "",
      category: null,
      company: null,
      function: null,
    });
    fetchDocuments();
  } catch (error) {
    setFormSubmitted(false);
    enqueueSnackbar("Failed to upload document", { variant: "error" });
  }
};



  return (
    <Box style={{ display: 'flex', height: '100vh' }}>
      {/* Left Pane */}
      <Box 
    style={{ 
        width: '250px', 
        backdropFilter: 'blur(10px)', 
        backgroundColor: 'rgba(255, 255, 255, 0.5)', 
        borderRadius: '10px 0 0 10px', // Top left and bottom left rounded
        padding: '16px', 
        borderTop: '1px solid #204060',    // Added top border
        borderBottom: '1px solid #204060', // Added bottom border
        borderLeft: '1px solid #204060',   // Added left border
        borderRight: 'none',  
    }}
>
<Typography variant="h6" style={{ color: '#204060' }}>
          Categories
          <IconButton onClick={handleAddCategory} style={{ marginLeft: '8px' }}>
          <AddIcon style={{ color: '#204060' }} />
          </IconButton>
        </Typography>
        <List>
          {categories.map((category) => (
            <ListItem 
              button 
              key={category.id} 
              onClick={() => handleCategoryClick(category)}
              onContextMenu={(event) => handleCategoryRightClick(event, category)}
            >
              <TopicIcon style={{ color: '#204060', marginRight: '8px' }} />
              <ListItemText primary={category.name} style={{ color: '#204060' }} />
            </ListItem>
          ))}
        </List>
      </Box>
       {/* Context Menu */}
       <Menu
  open={categoryContextMenu !== null}
  onClose={() => setCategoryContextMenu(null)}
  anchorReference="anchorPosition"
  anchorPosition={
    categoryContextMenu !== null
      ? { top: categoryContextMenu.mouseY, left: categoryContextMenu.mouseX }
      : undefined
  }
>
  <MenuItem onClick={() => handleEditCategory(categoryContextMenu.category)}>Edit Category</MenuItem>
  <MenuItem onClick={() => handleDeleteCategory(categoryContextMenu.category.id)}>Delete Category</MenuItem>
</Menu>
     
     {/* Add Category Dialog */}
     <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            value={categoryData.name}
            onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Category Description"
            value={categoryData.description}
            onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAddDocumentCategory}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            label="Category Name"
            value={categoryData.name}
            onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Category Description"
            value={categoryData.description}
            onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateDocumentCategory}>Update</Button>
        </DialogActions>
      </Dialog>

 {/* Right Pane */}
 <Box style={{ flexGrow: 1, padding: '16px', overflowY: 'auto', backgroundColor: '#204060', borderRadius: '0 10px 10px 0' }}>
 <Typography variant="h6" style={{ color: '#fff' }}>
          Documents
          <IconButton onClick={handleAddDocument} style={{ marginLeft: '8px' }}>
            <AddIcon style={{ color: '#fff' }} />
          </IconButton>
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2} justifyContent="flex-start">
            {filteredDocuments.map((doc) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={doc.id} style={{ margin: '8px' }}>
                <Paper
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '16px',
                    backgroundColor: '#fff',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                    width: '150px',
                    height: '150px',
                  }}
                  onClick={() => handleDocumentClick(doc)}
                  onContextMenu={(event) => handleDocumentRightClick(event, doc)}
                >
                  <Icon
                    {...getFileTypeIconProps({ extension: doc.filename.split('.').pop(), size: 48 })}
                    style={{ color: '#204060', marginBottom: '8px' }}
                  />
                  <Typography 
                    variant="body2" 
                    style={{ 
                      color: '#204060', 
                      textAlign: 'center', 
                      wordWrap: 'break-word',
                      maxWidth: '100%', 
                     fontFamily: "SapFiori72, sans-serif"
                    }}
                  >
                    {doc.filename}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      {/* Context Menu */}
      <Menu
  open={documentContextMenu !== null}
  onClose={() => setDocumentContextMenu(null)}
  anchorReference="anchorPosition"
  anchorPosition={
    documentContextMenu !== null
      ? { top: documentContextMenu.mouseY, left: documentContextMenu.mouseX }
      : undefined
  }
>
  <MenuItem onClick={() => handleView(documentContextMenu.document)}>View</MenuItem>
  <MenuItem onClick={() => handleEdit(documentContextMenu.document)}>Edit</MenuItem>
  <MenuItem onClick={() => handleDelete(documentContextMenu.document)}>Delete</MenuItem>
</Menu>

 {/* Add Document Dialog */}
 <Dialog open={openAddDocumentDialog} onClose={() => setOpenAddDocumentDialog(false)}>
        <DialogTitle>Add Document</DialogTitle>
        <DialogContent>
        <Typography variant="h6" style={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginBottom: 4 }}>Upload New Document</Typography>
            <TextField
            size="small"
              label="Document Name"
              value={newDocument.name}
              onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
              fullWidth
              margin="normal"
              required
              error={formSubmitted && !newDocument.name}
              helperText={formSubmitted && !newDocument.name ? 'Document name is required' : ''}
            />
            <TextField
            size="small"
              label="Description"
              multiline
              value={newDocument.description}
              onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
              fullWidth
              margin="normal"
            />
            <Autocomplete
  size="small"
  options={categories}
  getOptionLabel={(option) => option.name}
  value={newDocument.category || null}
  onChange={(e, newValue) => setNewDocument({ ...newDocument, category: newValue })}
  onOpen={handleCategoryDropdownOpen}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Category"
      fullWidth
      margin="normal"
      required
      error={formSubmitted && !newDocument.category}
      helperText={formSubmitted && !newDocument.category ? 'Document Category is required' : ''}
    />
  )}
/>

           {filteredCompanies.length > 1 && (
   <Autocomplete
   size="small"
   options={filteredCompanies}
   getOptionLabel={(option) => option.company_name}
   value={newDocument.company || defaultCompany}
   onChange={(e, newValue) => setNewDocument({ ...newDocument, company: newValue })}
   renderInput={(params) => <TextField {...params} label="Company" fullWidth margin="normal" />}
 />
 
  )}

  {filteredFunctions.length > 1 && (
   <Autocomplete
   size="small"
   options={filteredFunctions}
   getOptionLabel={(option) => option.function_name}
   value={newDocument.function || defaultFunction}
   onChange={(e, newValue) => setNewDocument({ ...newDocument, function: newValue })}
   renderInput={(params) => <TextField {...params} label="Function" fullWidth margin="normal" />}
 />
  )}
<Typography variant="caption">
              {newDocument.filename ? `Selected file: ${newDocument.filename}` : "No file selected"}
            </Typography>
<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ButtonContained
      variant="contained"
      onClick={handleButtonClick}
      style={{ paddingRight: '10px', paddingLeft: '10px'  }}
      {...( loading ? { disabled: true } : {})}

    >
      <AttachmentIcon /> Upload File
      <input
        type="file"
        hidden
  accept="*/*"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </ButtonContained>

              <ButtonOutlined
  variant="contained"
  color="primary"
  style={{ marginLeft: 8,  paddingRight: '10px', paddingLeft: '10px'  }}
  onClick={handleUpload}
  {...(!newDocument.file ? { disabled: true } : {})}
> Submit Document
</ButtonOutlined>

            </Box>
           
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDocumentDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)}>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          <Typography><strong>Name:</strong> {selectedDocument?.name}</Typography>
          <Typography><strong>Description:</strong> {selectedDocument?.description}</Typography>
          <Typography><strong>Relationship:</strong> {selectedDocument?.invoice_id ? "Invoice" : "General"}</Typography>
          <Typography><strong>Category:</strong> {selectedDocument?.category.name}</Typography>
          <Typography><strong>Uploaded By:</strong> {selectedDocument?.uploaded_by}</Typography>
          <Typography><strong>Company:</strong> {selectedDocument?.company.name}</Typography>
          <Typography><strong>Function:</strong> {selectedDocument?.function.name}</Typography>
          <Typography><strong>Uploaded At:</strong> {new Date(selectedDocument?.uploaded_at).toLocaleString()}</Typography>
          <Button onClick={() => handleAttachmentClick(selectedDocument)} variant="contained" color="primary">
            Attachment
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={selectedDocument?.name || ""}
            onChange={(e) => setSelectedDocument({ ...selectedDocument, name: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={selectedDocument?.description || ""}
            onChange={(e) => setSelectedDocument({ ...selectedDocument, description: e.target.value })}
            fullWidth
            margin="normal"
          />
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name}
            value={selectedDocument?.category || null}
            onChange={(e, newValue) => setSelectedDocument({ ...selectedDocument, category: newValue })}
            renderInput={(params) => <TextField {...params} label="Category" fullWidth margin="normal" />}
          />
          <TextField
            label="Filename"
            value={selectedDocument?.filename || ""}
            onChange={(e) => setSelectedDocument({ ...selectedDocument, filename: e.target.value })}
            fullWidth
            margin="normal"
          />
          <Box mt={2}>
            <Button onClick={handleSaveChanges} variant="contained" color="primary">Save Changes</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FileExplorer;
